import React, { useState, useEffect, useMemo } from 'react';
import { getProductVariants } from '../../utils/ecommerceHelper';
import { extractArticleImageNames, getArticleImageSlides } from '../../utils/imageHelper';

const EcommerceProductCard = ({
  product,
  canSeePrice,
  price,
  regularPrice,
  priceListLabel,
  selectedColorFilter = null,
  selectedSizeFilter = null,
  isLoggedIn = false,
  viewMode = 'grid',
  onAddToCart,
  onOpenDetail,
  onViewDetail,
  onLoginClick,
  imageFit,
  imageHeight
}) => {
  // Lista de talles del artículo (Rubro / mart060 / mstp030)
  const handleDetail = onOpenDetail || onViewDetail;
  const containerHeightStyle = imageHeight === 'compact' ? { height: '170px' } : imageHeight === 'large' ? { height: '265px' } : {};
  const imgFitStyle = imageFit ? { objectFit: imageFit } : {};

  const talles = useMemo(() => {
    return Array.isArray(product?.talles) ? product.talles : [];
  }, [product]);

  const hasTalles = talles.length > 0;
  const [selectedSize, setSelectedSize] = useState(null);

  // Auto-seleccionar talle según filtro activo o primer talle con stock
  useEffect(() => {
    if (!hasTalles) {
      setSelectedSize(null);
      return;
    }

    if (selectedSizeFilter && (Array.isArray(selectedSizeFilter) ? selectedSizeFilter.length > 0 : Boolean(selectedSizeFilter))) {
      const match = talles.find(t => {
        const tName = String(t.nombre).trim().toUpperCase();
        if (Array.isArray(selectedSizeFilter)) {
          return selectedSizeFilter.some(sf => String(sf).trim().toUpperCase() === tName);
        }
        return tName === String(selectedSizeFilter).trim().toUpperCase();
      });
      if (match) {
        setSelectedSize(match);
        return;
      }
    }

    const withStock = talles.find(t => Number(t.stock || 0) > 0);
    setSelectedSize(withStock || talles[0]);
  }, [hasTalles, talles, selectedSizeFilter]);

  // Stock efectivo: si maneja talles, toma el stock del talle seleccionado; sino el stock total q_stock
  const effectiveStock = useMemo(() => {
    if (hasTalles && selectedSize) {
      return Number(selectedSize.stock || 0);
    }
    return Number(product?.q_stock || 0);
  }, [hasTalles, selectedSize, product?.q_stock]);

  const hasStock = effectiveStock > 0;

  // Obtener variedades/colores normalizados del artículo
  const variants = useMemo(() => {
    return getProductVariants(product);
  }, [product]);

  const [selectedVariant, setSelectedVariant] = useState(variants[0]);
  const [quantity, setQuantity] = useState(1);
  const [isAddedAnimation, setIsAddedAnimation] = useState(false);
  const [hasImgError, setHasImgError] = useState(false);

  const imageCount = useMemo(() => extractArticleImageNames(product).length, [product]);

  // Auto-seleccionar la variante si coincide con el filtro de color activo
  useEffect(() => {
    if (selectedColorFilter && (Array.isArray(selectedColorFilter) ? selectedColorFilter.length > 0 : Boolean(selectedColorFilter)) && variants.length > 0) {
      const match = variants.find(v => {
        if (Array.isArray(selectedColorFilter)) {
          return selectedColorFilter.some(cf => 
            v.id === cf || (v.name && v.name.toLowerCase().includes(String(cf).toLowerCase()))
          );
        }
        return v.id === selectedColorFilter || 
               (v.name && v.name.toLowerCase().includes(selectedColorFilter.toLowerCase()));
      });
      if (match) {
        setSelectedVariant(match);
      }
    } else if (variants.length > 0) {
      setSelectedVariant(variants[0]);
    }
  }, [selectedColorFilter, variants]);

  const handleIncrement = (e) => {
    e.stopPropagation();
    setQuantity((prev) => (effectiveStock > 0 && prev >= effectiveStock ? effectiveStock : prev + 1));
  };

  const handleDecrement = (e) => {
    e.stopPropagation();
    setQuantity((prev) => (prev > 1 ? prev - 1 : 1));
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    if (effectiveStock > 0 && quantity > effectiveStock) {
      return;
    }
    onAddToCart({
      ...product,
      selectedVariant,
      selectedSize,
      quantity,
      maxStock: effectiveStock > 0 ? effectiveStock : undefined,
      unitPrice: finalPrice,
      canSeePrice
    });

    setIsAddedAnimation(true);
    setTimeout(() => setIsAddedAnimation(false), 1200);
  };

  const brandLabel = (product?.d_marca || product?.c_marca || '').trim();
  const hasBrand = Boolean(brandLabel && !['0', '00', '000', '-'].includes(brandLabel));

  const rubroLabel = (product?.d_rubro || '').trim();
  const hasRubro = Boolean(rubroLabel && !rubroLabel.toLowerCase().startsWith('rubro'));

  const familiaLabel = (product?.d_famil || '').trim();
  const hasFamilia = Boolean(familiaLabel && !['0', '00', '000', '-'].includes(familiaLabel) && familiaLabel.toLowerCase() !== rubroLabel.toLowerCase());

  // Resolver URLs candidatas para la imagen principal
  const candidateUrls = useMemo(() => {
    const slides = getArticleImageSlides(product);
    if (slides.length > 0 && slides[0].candidates && slides[0].candidates.length > 0) {
      return slides[0].candidates;
    }
    const fallback = product?.imagen || product?.foto || null;
    return fallback ? [fallback] : [];
  }, [product]);

  const [candidateIdx, setCandidateIdx] = useState(0);

  useEffect(() => {
    setCandidateIdx(0);
    setHasImgError(false);
  }, [product?.c_artic, product?.d_imagen, product?.imagen, product?.foto]);

  const currentImgUrl = !hasImgError && candidateUrls.length > 0 ? candidateUrls[candidateIdx] : null;

  const handleImgError = () => {
    if (candidateIdx < candidateUrls.length - 1) {
      setCandidateIdx(prev => prev + 1);
    } else {
      setHasImgError(true);
    }
  };

  // Detección de Oferta Especial (MART000.I_PREANT es el precio promocional a mostrar y cobrar)
  const offerPrice = Number(product?.i_preant ?? product?.I_PREANT ?? 0);
  const isOffer = offerPrice > 0;
  const finalPrice = isOffer ? offerPrice : price;
  const listPriceToStrike = Number(regularPrice || product?.i_conta || product?.i_lista || price || 0);

  if (viewMode === 'list') {
    return (
      <div className={`ecom-product-card ecom-product-card-list position-relative ${isOffer ? 'ecom-product-on-sale' : ''}`}>
        <div className="ecom-card-list-wrapper d-flex flex-column flex-md-row align-items-stretch align-items-md-center p-3 gap-3 gap-md-4">
          
          {/* 1. Columna Imagen */}
          <div 
            className="ecom-card-list-img-container cursor-pointer position-relative flex-shrink-0"
            onClick={() => handleDetail && handleDetail(product, selectedVariant, price, selectedSize)}
            style={{ cursor: 'pointer' }}
          >
            {isOffer && (
              <span className="badge bg-danger text-white fw-bold shadow-sm px-2 py-0.5 text-uppercase rounded-pill position-absolute top-0 start-0 m-2" style={{ zIndex: 2, fontSize: '0.65rem', letterSpacing: '0.5px' }}>
                <i className="bi bi-tag-fill"></i> OFERTA
              </span>
            )}
            {imageCount > 1 && (
              <span 
                className="position-absolute top-0 end-0 m-2 badge bg-dark bg-opacity-75 text-white rounded-pill px-1.5 py-0.5 shadow-sm"
                style={{ zIndex: 2, fontSize: '0.62rem' }}
                title={`${imageCount} fotos disponibles`}
              >
                <i className="bi bi-images me-1"></i>{imageCount}
              </span>
            )}
            {currentImgUrl ? (
              <img 
                src={currentImgUrl} 
                alt={product.d_artic} 
                className="ecom-card-list-img" 
                onError={handleImgError}
              />
            ) : (
              <div className="d-flex flex-column align-items-center justify-content-center text-muted opacity-50 h-100">
                <i className="bi bi-box-seam fs-2 mb-1"></i>
                <span className="small text-uppercase" style={{ fontSize: '0.6rem' }}>Sin Foto</span>
              </div>
            )}
          </div>

          {/* 2. Columna Información central */}
          <div className="ecom-card-list-info flex-grow-1 min-w-0">
            {/* Badges y Rubro / Familia */}
            <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
              {hasBrand && (
                <span className="badge bg-white shadow-xs text-dark border px-2 py-0.5 rounded-2" style={{ fontSize: '0.7rem', fontWeight: 600 }}>
                  {brandLabel}
                </span>
              )}
              {hasRubro && (
                <span className="ecom-card-brand text-muted" style={{ fontSize: '0.75rem' }}>
                  {rubroLabel}
                  {hasFamilia && <span className="text-dark fw-semibold"> › {familiaLabel}</span>}
                </span>
              )}
              {!hasRubro && hasFamilia && (
                <span className="ecom-card-brand text-muted fw-semibold" style={{ fontSize: '0.75rem' }}>
                  {familiaLabel}
                </span>
              )}
              {Number(product.q_unxbu || 0) > 1 ? (
                <span className="badge bg-light text-muted border small" style={{ fontSize: '0.68rem' }}>
                  Bulto x{product.q_unxbu}
                </span>
              ) : null}
            </div>

            {/* Título y Código */}
            <h3
              className="ecom-card-title mb-1"
              onClick={() => handleDetail && handleDetail(product, selectedVariant, price, selectedSize)}
              title={product.d_artic + (product.d_arti2 ? ` - ${product.d_arti2}` : '')}
            >
              {product.d_artic}
            </h3>
            {product.d_arti2 && (
              <p className="text-muted small text-truncate mb-1" style={{ fontSize: '0.8rem' }} title={product.d_arti2}>
                {product.d_arti2}
              </p>
            )}
            <div className="ecom-card-code mb-2">
              Cód: <span className="fw-semibold text-dark">{product.c_artic}</span>
              {product.c_barra && <span className="ms-2 text-muted">| EAN: {product.c_barra}</span>}
            </div>

            {/* Selector de Variedades / Colores y Talles en línea */}
            <div className="d-flex flex-wrap align-items-center gap-3 mt-1">
              {variants.length > 1 && (
                <div className="ecom-variants-container m-0">
                  <div className="ecom-variant-label mb-1" style={{ fontSize: '0.75rem' }}>
                    <span>Color: <strong>{selectedVariant?.name}</strong></span>
                  </div>
                  <div className="ecom-variant-pills">
                    {variants.map((v) => {
                      if (v.color || v.isRainbow || v.isTransparent) {
                        let dotClass = `ecom-color-dot ${selectedVariant?.id === v.id ? 'selected' : ''}`;
                        if (v.isRainbow) dotClass += ' ecom-color-swatch-rainbow';
                        if (v.isTransparent) dotClass += ' ecom-color-swatch-trans';
                        return (
                          <span
                            key={v.id}
                            className={dotClass}
                            style={{ backgroundColor: v.color || '#ffffff' }}
                            title={v.name}
                            onClick={() => setSelectedVariant(v)}
                          />
                        );
                      }
                      return (
                        <button
                          key={v.id}
                          type="button"
                          className={`ecom-variant-pill ${selectedVariant?.id === v.id ? 'selected' : ''}`}
                          onClick={() => setSelectedVariant(v)}
                        >
                          {v.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {hasTalles && (
                <div className="ecom-variants-container m-0">
                  <div className="ecom-variant-label mb-1" style={{ fontSize: '0.75rem' }}>
                    <span>Talle: <strong>{selectedSize?.nombre || 'Seleccionar'}</strong></span>
                  </div>
                  <div className="ecom-variant-pills d-flex flex-wrap gap-1">
                    {talles.map((t) => {
                      const isSelected = selectedSize?.id === t.id || selectedSize?.nombre === t.nombre;
                      const sizeStock = Number(t.stock || 0);
                      const isSizeInStock = sizeStock > 0;

                      return (
                        <button
                          key={t.id || t.nombre}
                          type="button"
                          className={`btn btn-sm py-0.5 px-2 rounded-2 fw-semibold ${
                            isSelected
                              ? 'btn-primary text-white shadow-sm'
                              : isSizeInStock
                              ? 'btn-outline-secondary border-light-subtle bg-light text-dark'
                              : 'btn-light text-muted border text-decoration-line-through opacity-60'
                          }`}
                          style={{ fontSize: '0.72rem', minWidth: '26px', height: '24px' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSize(t);
                          }}
                          title={isSizeInStock ? `Talle ${t.nombre} (Stock: ${sizeStock})` : `Talle ${t.nombre} (Sin stock)`}
                        >
                          {t.nombre}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 3. Columna Precio, Stock y Acciones */}
          <div className="ecom-card-list-actions d-flex flex-column align-items-start align-items-md-end justify-content-center text-md-end flex-shrink-0 pt-2 pt-md-0 border-top border-md-top-0 gap-2" style={{ minWidth: '220px' }}>
            {/* Bloque Precios */}
            <div className="w-100">
              {canSeePrice ? (
                <div>
                  {isOffer && (
                    <div className="d-flex align-items-center justify-content-start justify-content-md-end gap-1 mb-0.5">
                      <span className="text-muted text-decoration-line-through small fw-semibold" style={{ fontSize: '0.82rem' }}>
                        ${listPriceToStrike.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-1.5 py-0.5 rounded-1" style={{ fontSize: '0.62rem', fontWeight: 700 }}>
                        LISTA
                      </span>
                    </div>
                  )}

                  <div className="d-flex align-items-baseline justify-content-start justify-content-md-end gap-2">
                    <span className={`ecom-price-value fs-4 ${isOffer ? 'text-danger fw-bolder' : ''}`}>
                      ${Number(finalPrice || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                    {priceListLabel && !isOffer && (
                      <span className="badge bg-primary-subtle text-primary border border-primary-subtle" style={{ fontSize: '0.68rem' }}>
                        {priceListLabel}
                      </span>
                    )}
                    {isOffer && (
                      <span className="badge bg-danger text-white shadow-xs" style={{ fontSize: '0.65rem' }}>
                        OFERTA
                      </span>
                    )}
                  </div>
                  <span className="text-muted small d-block" style={{ fontSize: '0.72rem' }}>
                    Precio unitario s/IVA
                  </span>
                </div>
              ) : (
                <div
                  className="ecom-price-locked p-2"
                  onClick={onLoginClick}
                  title="Registrate o iniciá sesión para ver precios exclusivos"
                >
                  <div className="d-flex align-items-center gap-1.5">
                    <i className="bi bi-lock-fill text-warning"></i>
                    <span className="fw-semibold small">Registrate para ver precio</span>
                  </div>
                  <i className="bi bi-chevron-right small text-muted"></i>
                </div>
              )}
            </div>

            {/* Stock indicator */}
            {isLoggedIn && (
              <div className="w-100 text-start text-md-end">
                {hasStock ? (
                  <span className="small text-muted d-inline-flex align-items-center gap-1" style={{ fontSize: '0.76rem' }}>
                    <i className="bi bi-box-seam text-success"></i>
                    <span>
                      Stock: <strong className="text-dark">{Number(effectiveStock).toLocaleString('es-AR')}</strong>
                      {hasTalles && selectedSize ? ` (Talle ${selectedSize.nombre})` : ''}
                    </span>
                  </span>
                ) : (
                  <span className="small text-danger fw-bold d-inline-flex align-items-center gap-1" style={{ fontSize: '0.78rem' }}>
                    <i className="bi bi-x-circle-fill"></i>
                    <span>Sin stock{hasTalles && selectedSize ? ` en ${selectedSize.nombre}` : ''}</span>
                  </span>
                )}
              </div>
            )}

            {/* Acciones: Cantidad y Botón */}
            <div className="d-flex align-items-center gap-2 w-100 mt-1">
              <div className={`ecom-qty-picker ${!hasStock ? 'opacity-50 pe-none' : ''}`}>
                <button
                  type="button"
                  className="ecom-qty-btn"
                  onClick={handleDecrement}
                  title="Disminuir"
                  disabled={!hasStock || quantity <= 1}
                >
                  <i className="bi bi-dash"></i>
                </button>
                <input
                  type="text"
                  className="ecom-qty-input"
                  value={hasStock ? quantity : 0}
                  disabled={!hasStock}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (isNaN(val) || val < 1) {
                      setQuantity(1);
                    } else if (effectiveStock > 0 && val > effectiveStock) {
                      setQuantity(effectiveStock);
                    } else {
                      setQuantity(val);
                    }
                  }}
                />
                <button
                  type="button"
                  className="ecom-qty-btn"
                  onClick={handleIncrement}
                  title="Aumentar"
                  disabled={!hasStock || (effectiveStock > 0 && quantity >= effectiveStock)}
                >
                  <i className="bi bi-plus"></i>
                </button>
              </div>

              <button
                type="button"
                className={`btn flex-grow-1 ecom-card-action-btn d-flex align-items-center justify-content-center gap-1.5 fw-semibold rounded-3 shadow-sm py-2 ${
                  !hasStock
                    ? 'btn-secondary disabled opacity-75'
                    : !isLoggedIn
                    ? 'btn-outline-primary bg-white'
                    : isAddedAnimation
                    ? 'btn-success text-white'
                    : 'btn-primary'
                }`}
                onClick={
                  !hasStock
                    ? undefined
                    : !isLoggedIn
                    ? (e) => {
                        e.stopPropagation();
                        if (onLoginClick) onLoginClick();
                        else handleAddToCart(e);
                      }
                    : handleAddToCart
                }
                disabled={!hasStock}
                title={!hasStock ? 'Artículo sin stock disponible' : !isLoggedIn ? 'Iniciá sesión para comprar' : 'Agregar al carrito'}
              >
                {!hasStock ? (
                  <>
                    <i className="bi bi-slash-circle"></i>
                    <span>Sin stock</span>
                  </>
                ) : !isLoggedIn ? (
                  <>
                    <i className="bi bi-lock-fill"></i>
                    <span className="small">Ingresá</span>
                  </>
                ) : isAddedAnimation ? (
                  <>
                    <i className="bi bi-check-circle-fill"></i>
                    <span>¡Agregado!</span>
                  </>
                ) : (
                  <>
                    <i className="bi bi-cart-plus-fill"></i>
                    <span>Agregar</span>
                  </>
                )}
              </button>
            </div>

          </div>

        </div>
      </div>
    );
  }

  return (
    <div className={`ecom-product-card position-relative ${isOffer ? 'ecom-product-on-sale' : ''}`}>
      {/* Badges superiores: Oferta y Marca / Rubro */}
      <div className="position-absolute top-0 start-0 m-2 d-flex flex-column gap-1" style={{ zIndex: 3 }}>
        {isOffer && (
          <span className="badge bg-danger text-white fw-bold shadow-sm px-2.5 py-1 text-uppercase rounded-pill d-inline-flex align-items-center gap-1" style={{ fontSize: '0.72rem', letterSpacing: '0.6px' }}>
            <i className="bi bi-tag-fill"></i> OFERTA
          </span>
        )}
        {hasBrand && (
          <span className="ecom-card-badge-inline bg-white shadow-sm text-dark border px-2 py-0.5 rounded-2" style={{ fontSize: '0.72rem', fontWeight: 600 }}>
            {brandLabel}
          </span>
        )}
      </div>

      {/* Badge de galería si tiene más de 1 imagen */}
      {imageCount > 1 && (
        <span 
          className="position-absolute top-0 end-0 m-2 badge bg-dark bg-opacity-75 text-white rounded-pill px-2 py-1 shadow-sm"
          style={{ zIndex: 2, fontSize: '0.68rem', letterSpacing: '0.3px' }}
          title={`${imageCount} fotos disponibles`}
        >
          <i className="bi bi-images me-1"></i>{imageCount}
        </span>
      )}

      {/* Imagen del Artículo */}
      <div 
        className="ecom-card-img-container cursor-pointer"
        onClick={() => handleDetail && handleDetail(product, selectedVariant, price, selectedSize)}
        style={{ cursor: 'pointer', ...containerHeightStyle }}
      >
        {currentImgUrl ? (
          <img 
            src={currentImgUrl} 
            alt={product.d_artic} 
            className="ecom-card-img"
            style={imgFitStyle}
            onError={handleImgError}
          />
        ) : (
          <div className="d-flex flex-column align-items-center justify-content-center text-muted opacity-50 h-100">
            <i className="bi bi-box-seam display-4 mb-1"></i>
            <span className="small text-uppercase" style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>
              Sin Foto
            </span>
          </div>
        )}
      </div>

      {/* Cuerpo de la Tarjeta */}
      <div className="ecom-card-body">
        {/* Marca / Categoría / Subrubro */}
        <div className="d-flex align-items-center justify-content-between">
          <span className="ecom-card-brand text-truncate me-1" title={hasRubro && hasFamilia ? `${rubroLabel} › ${familiaLabel}` : (hasRubro ? rubroLabel : familiaLabel)}>
            {hasRubro ? (
              <>
                {rubroLabel}
                {hasFamilia && <span className="text-dark fw-semibold"> › {familiaLabel}</span>}
              </>
            ) : (
              hasFamilia ? familiaLabel : ''
            )}
          </span>
          {Number(product.q_unxbu || 0) > 1 ? (
            <span className="badge bg-light text-muted border small flex-shrink-0" style={{ fontSize: '0.68rem' }}>
              Bulto x{product.q_unxbu}
            </span>
          ) : null}
        </div>

        {/* Título y Código */}
        <h3
          className="ecom-card-title"
          onClick={() => handleDetail && handleDetail(product, selectedVariant, price, selectedSize)}
          title={product.d_artic + (product.d_arti2 ? ` - ${product.d_arti2}` : '')}
        >
          {product.d_artic}
        </h3>
        {product.d_arti2 && (
          <p className="text-muted small text-truncate mb-1" style={{ fontSize: '0.78rem' }} title={product.d_arti2}>
            {product.d_arti2}
          </p>
        )}
        <div className="ecom-card-code">
          Cód: <span className="fw-semibold text-dark">{product.c_artic}</span>
          {product.c_barra && <span className="ms-2 text-muted">| EAN: {product.c_barra}</span>}
        </div>

        {/* Selector de Variedades / Colores */}
        {variants.length > 1 && (
          <div className="ecom-variants-container">
            <div className="ecom-variant-label">
              <span>Variante: <strong>{selectedVariant?.name}</strong></span>
            </div>
            <div className="ecom-variant-pills">
              {variants.map((v) => {
                if (v.color || v.isRainbow || v.isTransparent) {
                  let dotClass = `ecom-color-dot ${selectedVariant?.id === v.id ? 'selected' : ''}`;
                  if (v.isRainbow) dotClass += ' ecom-color-swatch-rainbow';
                  if (v.isTransparent) dotClass += ' ecom-color-swatch-trans';
                  return (
                    <span
                      key={v.id}
                      className={dotClass}
                      style={{ backgroundColor: v.color || '#ffffff' }}
                      title={v.name}
                      onClick={() => setSelectedVariant(v)}
                    />
                  );
                }
                return (
                  <button
                    key={v.id}
                    type="button"
                    className={`ecom-variant-pill ${selectedVariant?.id === v.id ? 'selected' : ''}`}
                    onClick={() => setSelectedVariant(v)}
                  >
                    {v.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Selector de Talles / Medidas */}
        {hasTalles && (
          <div className="ecom-variants-container mt-1">
            <div className="ecom-variant-label">
              <span>Talle: <strong>{selectedSize?.nombre || 'Seleccionar'}</strong></span>
            </div>
            <div className="ecom-variant-pills d-flex flex-wrap gap-1">
              {talles.map((t) => {
                const isSelected = selectedSize?.id === t.id || selectedSize?.nombre === t.nombre;
                const sizeStock = Number(t.stock || 0);
                const isSizeInStock = sizeStock > 0;

                return (
                  <button
                    key={t.id || t.nombre}
                    type="button"
                    className={`btn btn-sm py-0.5 px-2 rounded-2 fw-semibold ${
                      isSelected
                        ? 'btn-primary text-white shadow-sm'
                        : isSizeInStock
                        ? 'btn-outline-secondary border-light-subtle bg-light text-dark'
                        : 'btn-light text-muted border text-decoration-line-through opacity-60'
                    }`}
                    style={{ fontSize: '0.75rem', minWidth: '28px', height: '26px' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedSize(t);
                    }}
                    title={isSizeInStock ? `Talle ${t.nombre} (Stock: ${sizeStock})` : `Talle ${t.nombre} (Sin stock)`}
                  >
                    {t.nombre}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Bloque de Precios (Condicional según Login y Configuración) */}
        <div className="ecom-price-container">
          {canSeePrice ? (
            <div>
              {/* Precio Regular de Lista Tachado si es Oferta */}
              {isOffer && (
                <div className="d-flex align-items-center gap-1 mb-0.5">
                  <span className="text-muted text-decoration-line-through small fw-semibold" style={{ fontSize: '0.82rem' }}>
                    ${listPriceToStrike.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-1.5 py-0.5 rounded-1" style={{ fontSize: '0.65rem', fontWeight: 700 }}>
                    LISTA
                  </span>
                </div>
              )}

              <div className="d-flex align-items-baseline justify-content-between">
                <span className={`ecom-price-value ${isOffer ? 'text-danger fw-bolder' : ''}`}>
                  ${Number(finalPrice || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                {priceListLabel && !isOffer && (
                  <span className="badge bg-primary-subtle text-primary border border-primary-subtle" style={{ fontSize: '0.7rem' }}>
                    {priceListLabel}
                  </span>
                )}
                {isOffer && (
                  <span className="badge bg-danger text-white shadow-xs" style={{ fontSize: '0.68rem', letterSpacing: '0.3px' }}>
                    OFERTA
                  </span>
                )}
              </div>
              <span className="text-muted small" style={{ fontSize: '0.72rem' }}>
                Precio unitario s/IVA
              </span>
            </div>
          ) : (
            <div
              className="ecom-price-locked"
              onClick={onLoginClick}
              title="Registrate o iniciá sesión para ver precios exclusivos"
            >
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-lock-fill text-warning fs-6"></i>
                <span className="fw-semibold">Registrate para ver precio</span>
              </div>
              <i className="bi bi-chevron-right small text-muted"></i>
            </div>
          )}
        </div>

        {/* Indicador de Stock (Visible solo si el usuario está autenticado) */}
        {isLoggedIn && (
          <div className="mt-1 mb-1">
            {hasStock ? (
              <span className="small text-muted d-inline-flex align-items-center gap-1" style={{ fontSize: '0.78rem' }}>
                <i className="bi bi-box-seam text-success"></i>
                <span>
                  Stock disponible: <strong className="text-dark">{Number(effectiveStock).toLocaleString('es-AR')}</strong>
                  {hasTalles && selectedSize ? ` (Talle ${selectedSize.nombre})` : ''}
                </span>
              </span>
            ) : (
              <span className="small text-danger fw-bold d-inline-flex align-items-center gap-1" style={{ fontSize: '0.82rem' }}>
                <i className="bi bi-x-circle-fill"></i>
                <span>Sin stock{hasTalles && selectedSize ? ` en talle ${selectedSize.nombre}` : ''}</span>
              </span>
            )}
          </div>
        )}

        {/* Acciones de Compra y Cantidad */}
        <div className="d-flex align-items-center gap-2 mt-2">
          {/* Selector de Cantidad */}
          <div className={`ecom-qty-picker ${!hasStock ? 'opacity-50 pe-none' : ''}`}>
            <button
              type="button"
              className="ecom-qty-btn"
              onClick={handleDecrement}
              title="Disminuir"
              disabled={!hasStock || quantity <= 1}
            >
              <i className="bi bi-dash"></i>
            </button>
            <input
              type="text"
              className="ecom-qty-input"
              value={hasStock ? quantity : 0}
              disabled={!hasStock}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                if (isNaN(val) || val < 1) {
                  setQuantity(1);
                } else if (effectiveStock > 0 && val > effectiveStock) {
                  setQuantity(effectiveStock);
                } else {
                  setQuantity(val);
                }
              }}
            />
            <button
              type="button"
              className="ecom-qty-btn"
              onClick={handleIncrement}
              title="Aumentar"
              disabled={!hasStock || (effectiveStock > 0 && quantity >= effectiveStock)}
            >
              <i className="bi bi-plus"></i>
            </button>
          </div>

          {/* Botón Agregar al Carrito */}
          <button
            type="button"
            className={`btn flex-grow-1 ecom-card-action-btn d-flex align-items-center justify-content-center gap-2 fw-semibold rounded-3 shadow-sm ${
              !hasStock
                ? 'btn-secondary disabled opacity-75'
                : !isLoggedIn
                ? 'btn-outline-primary bg-white'
                : isAddedAnimation
                ? 'btn-success text-white'
                : 'btn-primary'
            }`}
            onClick={
              !hasStock
                ? undefined
                : !isLoggedIn
                ? (e) => {
                    e.stopPropagation();
                    if (onLoginClick) onLoginClick();
                    else handleAddToCart(e);
                  }
                : handleAddToCart
            }
            disabled={!hasStock}
            title={!hasStock ? 'Artículo sin stock disponible' : !isLoggedIn ? 'Iniciá sesión para comprar' : 'Agregar al carrito'}
          >
            {!hasStock ? (
              <>
                <i className="bi bi-slash-circle"></i>
                <span>Sin stock</span>
              </>
            ) : !isLoggedIn ? (
              <>
                <i className="bi bi-lock-fill"></i>
                <span>Ingresá para comprar</span>
              </>
            ) : isAddedAnimation ? (
              <>
                <i className="bi bi-check-circle-fill"></i>
                <span>¡Agregado!</span>
              </>
            ) : (
              <>
                <i className="bi bi-cart-plus-fill"></i>
                <span>Agregar</span>
              </>
            )}
          </button>
        </div>

      </div>
    </div>
  );
};

export default EcommerceProductCard;
